import React, { useCallback, useRef, useState } from 'react';
import Spinner from 'components/Spinner';
import { Switch, SwitchWithNotification } from 'components/Switch/Switch';
import { CollectionSidebar } from './CollectionSidebar';
import styles from './CollectionView.module.scss';
import {
  CollectionFilter,
  collectionCategoryData,
  DEFAULT_COLLECTION_NFT_CATEGORY,
  toNftType,
  useCollection,
} from './fetcher';
import { CollectionGrid } from './CollectionGrid';
import { useUrlParamState } from 'utils/url';
import { routes } from 'routing/routes';
import { CollectionContext, useCollectionContext } from './context';
import {
  CollectionEntity,
  CollectionEntities,
  CollectionFetchType,
  CollectionStatsRaw,
} from './model';
import Skeleton from 'react-loading-skeleton';
import { HatchCTA } from '../YourCollection/HatchCTA';
import { gtag } from '../../utils/analytics';
import { Egg } from '../Egg/Egg';
import PairBreaker from 'components/Egg/PairBreaker';
import { useLoginRedirect } from 'hooks/useLoginRedirect';
import { CollectionEmptyState } from './CollectionEmptyState';
import cls from 'classnames';
import ReactPerfectScrollbar from 'react-perfect-scrollbar';

const filterCountSpinner = <Spinner width={16} height={16} color="white" />;

function HatcheningCTA({ filterType }: { filterType: CollectionFetchType }) {
  const { data } = useCollection(filterType);

  // we only consider the first page of results otherwise we will
  // load a CTA at the top after the user has scrolled to the bottom
  const entities = data?.pages?.[0].items;

  const shouldShowHatcheningCTA =
    ['all', CollectionEntities.Eggs].includes(filterType) &&
    entities?.some((entity) => entity.type === 'eggs' && !entity.item.hatching);

  if (!shouldShowHatcheningCTA) {
    return null;
  }

  function onClickCTA() {
    gtag('event', 'collection hatchening CTA click');
  }

  return <HatchCTA onClick={onClickCTA} to={routes.hatchening()} />;
}

type CollectionProps = {
  loadMore: () => void;
};

export const Collection = ({ loadMore }: CollectionProps) => {
  const { setFilter, filter, allFilters } = useCollectionContext();
  const currentFilterData = collectionCategoryData[filter.type];

  const header = (
    <header className={styles.header}>
      <div>
        <h1 className="mb-2 heading-default text-100">Your Collection</h1>
        <p className="m-0 body-large text-60">Explore your DuskBreakers NFTs</p>
      </div>
      <div style={{ overflow: 'auto' }}>
        <Switch
          initialSelection={filter.type}
          items={allFilters.map((filter) => ({
            id: filter.type,
            children: (
              <SwitchWithNotification text={collectionCategoryData[filter.type].displayName}>
                {filter.count === undefined ? filterCountSpinner : filter.count}
              </SwitchWithNotification>
            ),
          }))}
          onChange={(item) => setFilter(toNftType(item.id).id)}
        />
      </div>
    </header>
  );
  if (filter.count === 0) {
    return (
      <main className={styles.collectionViewParent}>
        {header}
        <CollectionEmptyState filterType={filter.type} />
      </main>
    );
  } else {
    return (
      <main className={cls(styles.collectionViewParent)}>
        {header}
        <div className={styles.collectionView}>
          <ReactPerfectScrollbar className={styles.gallery} onYReachEnd={loadMore}>
            <h2 className="m-0 subheading-default">
              {currentFilterData.displayName} - {filter.count ?? <Skeleton width={25} />}
            </h2>
            <HatcheningCTA filterType={filter.type} />
            <CollectionGrid />
          </ReactPerfectScrollbar>
          <ReactPerfectScrollbar className={styles.sidebar}>
            <CollectionSidebar />
          </ReactPerfectScrollbar>
        </div>
      </main>
    );
  }
};

type CollectionViewProps = {
  filter: CollectionFetchType;
  setFilter(type: CollectionFetchType): void;
};

export function CollectionView(props: CollectionViewProps) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useCollection(props.filter);
  const cachedCollectionTotals = useRef<CollectionStatsRaw | undefined>(undefined);

  const stats = data ? data.pages.at(-1)?.collectionTotals : cachedCollectionTotals.current;

  // updating the cache to make sure we're always in-sync
  cachedCollectionTotals.current = stats;

  const filters: CollectionFilter[] = [
    {
      type: CollectionEntities.Duskbreakers,
      // Doing it like this because a collection might not be included in the response
      count: stats ? stats.duskbreakers ?? 0 : undefined,
    },
    {
      type: CollectionEntities.Gribbles,
      count: stats ? stats.gribbles ?? 0 : undefined,
    },
    {
      type: CollectionEntities.Eggs,
      count: stats ? stats.eggs ?? 0 : undefined,
    },
  ];

  // extra field for selecting everything
  filters.unshift({
    type: 'all',
    count: stats ? filters.reduce((count, filter) => (filter.count ?? 0) + count, 0) : undefined,
  });

  const selectedFilter = filters.find((filter) => filter.type === props.filter);

  if (!selectedFilter) {
    throw Error("User selected a filter that doesn't exist. Impossible state");
  }

  const [pairingModalOpen, setPairingModalOpen] = useState(false);
  const { data: response } = useCollection(selectedFilter.type);
  const [_selected, _setSelected] = useState<
    Record<CollectionFetchType, CollectionEntity | undefined>
  >({
    eggs: undefined,
    duskbreakers: undefined,
    all: undefined,
    gribbles: undefined,
  });

  const items = response?.pages.flatMap((page) => page.items);

  const selected = _selected[props.filter] ?? items?.[0];

  function setFilter(filter: CollectionFetchType) {
    props.setFilter(filter);
  }

  function setSelected(selected: CollectionEntity) {
    _setSelected((prev) => ({ ...prev, [props.filter]: selected }));
  }

  const loadMore = () => {
    if (isFetchingNextPage || !hasNextPage) {
      return;
    }
    fetchNextPage();
  };

  return (
    <CollectionContext.Provider
      value={{
        allFilters: filters,
        filter: selectedFilter,
        setFilter,
        selected,
        setSelected,
        pairingModalOpen,
        setPairingModalOpen,
      }}
    >
      <Collection loadMore={loadMore} />
      <CollectionBreakerPairing />
    </CollectionContext.Provider>
  );
}

function CollectionBreakerPairing() {
  const { pairingModalOpen, setPairingModalOpen, selected } = useCollectionContext();
  if (
    !pairingModalOpen ||
    !selected ||
    (selected.type !== 'gribbles' && selected.type !== 'eggs')
  ) {
    return null;
  }
  return <PairBreaker gribbleId={selected?.item.id} onClose={() => setPairingModalOpen(false)} />;
}

// Extra component to prevent rerendering <Egg> components when inner hooks change
export function CollectionRoute() {
  return (
    <Egg fullscreen={true}>
      <div className="mt-4 match-parent-fullscreen">
        <CollectionRouteBase />
      </div>
    </Egg>
  );
}

export function CollectionRouteBase() {
  // Separating the route/url part of the state from the state managed
  // by the component itself
  const [filterType, setFilterType] = useUrlParamState<CollectionFetchType>({
    name: 'filter',
    defaultValue: DEFAULT_COLLECTION_NFT_CATEGORY.id,
    serialize: (item) => item,
    deserialize: (item) => toNftType(item).id,
  });

  const onChange = useCallback(
    (type: CollectionFetchType) => {
      setFilterType(type, routes.collection.page({ filter: type }));
    },
    [setFilterType],
  );

  useLoginRedirect({ follow: routes.collection.base() });

  return <CollectionView filter={filterType} setFilter={onChange} />;
}
